import React from 'react';
import { Clock, User, Wrench, TrendingUp, TrendingDown, DollarSign } from 'lucide-react';
import { Quote } from '../../../models/workItem';

interface InspectionQuoteSummaryProps {
  quote: Quote;
}

const InspectionQuoteSummary: React.FC<InspectionQuoteSummaryProps> = ({ quote }) => {
  // Aggregate Fxer estimate from the quote
  const fxerEstimate = quote.analysis.lineItems.reduce(
    (acc, lineItem) => {
      const laborCosts = lineItem.analysis.subTasks.reduce(
        (subAcc, subTask) => {
          const subLaborLow = subTask.options.reduce(
            (optAcc, option) =>
              optAcc + option.expectedLaborHoursLow * option.laborCostLowPerHour,
            0
          );
          const subLaborHigh = subTask.options.reduce(
            (optAcc, option) =>
              optAcc + option.expectedLaborHoursHigh * option.laborCostHighPerHour,
            0
          );
          return {
            low: subAcc.low + subLaborLow,
            high: subAcc.high + subLaborHigh,
          };
        },
        { low: 0, high: 0 }
      );

      const materialCosts = lineItem.analysis.subTasks.reduce(
        (subAcc, subTask) => {
          const subMaterialLow = subTask.options.reduce(
            (optAcc, option) =>
              optAcc +
              option.materials.reduce(
                (matAcc, material) =>
                  matAcc + material.lowCostPerUnit * material.quantityLow,
                0
              ),
            0
          );
          const subMaterialHigh = subTask.options.reduce(
            (optAcc, option) =>
              optAcc +
              option.materials.reduce(
                (matAcc, material) =>
                  matAcc + material.highCostPerUnit * material.quantityHigh,
                0
              ),
            0
          );
          return {
            low: subAcc.low + subMaterialLow,
            high: subAcc.high + subMaterialHigh,
          };
        },
        { low: 0, high: 0 }
      );

      const laborLow = laborCosts.low;
      const laborHigh = laborCosts.high;
      const materialLow = materialCosts.low;
      const materialHigh = materialCosts.high;

      return {
        laborLow: acc.laborLow + laborLow,
        laborHigh: acc.laborHigh + laborHigh,
        materialLow: acc.materialLow + materialLow,
        materialHigh: acc.materialHigh + materialHigh,
        totalHoursLow: acc.totalHoursLow + lineItem.analysis.subTasks.reduce(
          (sum, subTask) =>
            sum +
            subTask.options.reduce(
              (optSum, option) => optSum + option.expectedLaborHoursLow,
              0
            ),
          0
        ),
        totalHoursHigh: acc.totalHoursHigh + lineItem.analysis.subTasks.reduce(
          (sum, subTask) =>
            sum +
            subTask.options.reduce(
              (optSum, option) => optSum + option.expectedLaborHoursHigh,
              0
            ),
          0
        ),
        totalLow: Math.ceil(acc.totalLow + laborLow + materialLow),
        totalHigh: Math.ceil(acc.totalHigh + laborHigh + materialHigh),
      };
    },
    {
      laborLow: 0,
      laborHigh: 0,
      materialLow: 0,
      materialHigh: 0,
      totalHoursLow: 0,
      totalHoursHigh: 0,
      totalLow: 0,
      totalHigh: 0,
    }
  );

  const formatRange = (low: number, high: number, unit = '', prefix = '') =>
    low === high
      ? `${prefix}${low.toLocaleString()}${unit ? ` ${unit}` : ''}`
      : `${prefix}${low.toLocaleString()} - ${prefix}${high.toLocaleString()}${unit ? ` ${unit}` : ''}`;

  // Calculate comparison with user-provided quote if available
  const showComparison = quote.analysis.quoteTotalCost > 0;
  const comparisonLow = showComparison 
    ? Math.round(((fxerEstimate.totalLow - quote.analysis.quoteTotalCost) / quote.analysis.quoteTotalCost) * 100) 
    : 0;
  const comparisonHigh = showComparison 
    ? Math.round(((fxerEstimate.totalHigh - quote.analysis.quoteTotalCost) / quote.analysis.quoteTotalCost) * 100)
    : 0;

  // For the summary cards - using white, grey, and Fxer blue colors
  const costItems = [
    {
      label: 'Labor',
      icon: <User className="h-4 w-4 text-fxer-blue" />,
      value: formatRange(fxerEstimate.laborLow, fxerEstimate.laborHigh, '', '$'),
      bgColor: 'bg-white border-gray-200',
      iconBg: 'bg-white',
      labelColor: 'text-gray-700',
      textColor: 'text-gray-800',
      hours: fxerEstimate.totalHoursLow.toFixed(1) + ' - ' + fxerEstimate.totalHoursHigh.toFixed(1) + ' hrs'
    },
    {
      label: 'Materials',
      icon: <Wrench className="h-4 w-4 text-fxer-blue" />,
      value: formatRange(fxerEstimate.materialLow, fxerEstimate.materialHigh, '', '$'),
      bgColor: 'bg-gray-50 border-gray-200',
      iconBg: 'bg-white',
      labelColor: 'text-gray-700',
      textColor: 'text-gray-800',
    },
    {
      label: 'Estimated Cost',
      icon: <DollarSign className="h-4 w-4 text-white" />,
      value: formatRange(fxerEstimate.totalLow, fxerEstimate.totalHigh, '', '$'),
      bgColor: 'bg-fxer-blue border-fxer-blue',
      iconBg: 'bg-white/20',
      labelColor: 'text-white',
      textColor: 'text-white',
      highlight: true
    }
  ];

  return (
    <div className="px-6 mb-8">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {costItems.map((item, index) => (
          <div 
            key={index} 
            className={`rounded-lg border p-4 ${item.bgColor} ${item.highlight ? 'shadow-sm' : ''}`}
          >
            <div className="flex justify-between items-start">
              <div className="flex items-center gap-2">
                <div className={`p-1.5 rounded-full ${item.iconBg} shadow-sm`}>
                  {item.icon}
                </div>
                <h3 className={`text-sm font-semibold ${item.labelColor}`}>{item.label}</h3>
              </div>
              {item.hours && (
                <div className={`text-xs ${item.highlight ? 'text-white/70' : 'text-gray-500'} flex items-center`}>
                  <Clock className="h-3 w-3 mr-1" />
                  {item.hours}
                </div>
              )}
            </div>
            <div className={`mt-2 text-xl font-bold ${item.textColor}`}>
              {item.value}
            </div>
          </div>
        ))}
      </div>

      {/* User Quote Comparison */}
      {showComparison && (
        <div className="mt-5 p-4 border border-gray-200 rounded-lg">
          <div className="flex flex-col md:flex-row md:items-center justify-between">
            <div className="mb-3 md:mb-0">
              <h3 className="text-sm font-medium text-gray-700 mb-1">User-Provided Quote</h3>
              <div className="flex items-baseline">
                <span className="text-2xl font-bold text-gray-900">
                  ${quote.analysis.quoteTotalCost.toLocaleString()}
                </span>
              </div>
            </div>
            
            <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
              <h4 className="text-xs font-medium text-gray-500 mb-1">Fxer Estimate Comparison</h4>
              <div className="flex items-center gap-2">
                {comparisonLow < 0 ? (
                  <div className="flex items-center text-green-600">
                    <TrendingDown className="h-4 w-4 mr-1" />
                    <span className="font-semibold">
                      {Math.abs(comparisonLow)}% - {Math.abs(comparisonHigh)}% lower
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center text-amber-600">
                    <TrendingUp className="h-4 w-4 mr-1" />
                    <span className="font-semibold">
                      {Math.abs(comparisonLow)}% - {Math.abs(comparisonHigh)}% higher
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InspectionQuoteSummary;